import '@main/polyfills/promise-with-resolvers';

import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  ButtonProps,
  useDisclosure,
} from '@chakra-ui/react';
import { Button } from '@main/complykit/components/button';
import { useStableCallback } from '@main/shared/browser';
import { isAbortError } from '@main/shared/utils';
import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export interface AlertDialogConfig<T = unknown> {
  dialogHeader: string;
  dialogContent: ReactNode;
  cancelBtnLabel?: string;
  /* Press-invoked onClick configs: no event, and presses do not bubble (see
   * actions/types.ts). */
  cancelAction?: Omit<ButtonProps, 'onClick'> & {
    onClick?: () => void;
  };
  confirmAction?: Omit<ButtonProps, 'onClick'> & {
    onClick?: () => Promise<T> | T;
  };
}

export type AlertDialogContextType = {
  openDialog<T = unknown>(state: AlertDialogConfig<T>): Promise<T>;
  closeDialog(): void;
};

export const defaultAlertDialogContext: AlertDialogContextType = {
  openDialog<T = unknown>() {
    return Promise.resolve<T>(undefined as T);
  },
  closeDialog: () => null,
};

interface AlertDialogState extends AlertDialogConfig {
  isLoading: boolean;
  canConfirm: boolean;
}

const defaultState: AlertDialogState = {
  isLoading: false,
  canConfirm: true,
  dialogHeader: '',
  dialogContent: '',
};

const AlertDialogContext = createContext<AlertDialogContextType>(defaultAlertDialogContext);

export interface AlertDialogRef {
  setResult(result: unknown): void;
  setCanConfirm(canConfirm: boolean): void;
  closeDialog(): void;
}

const AlertDialogRefContext = createContext<AlertDialogRef>({
  setResult: () => void 0,
  setCanConfirm: () => void 0,
  closeDialog: () => void 0,
});

export const AlertDialogContainer = ({ children }: { children?: ReactNode }) => {
  const { t } = useTranslation('ui');
  const resolversRef = useRef(createPromiseResolvers());
  const { isOpen, onOpen, onClose } = useDisclosure({
    onClose: () => resolversRef.current.reject(new Error('AbortError')),
  });
  //chakra-ui.com/docs/components/alert-dialog/usage#usage
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [state, setState] = useState<AlertDialogState>(defaultState);
  const resultRef = useRef<unknown>();

  const openDialog = useStableCallback((newState: AlertDialogConfig) => {
    resolversRef.current = createPromiseResolvers();
    setState({ ...defaultState, ...newState });
    onOpen();
    return resolversRef.current.promise;
  }) as <T>(state: AlertDialogConfig<T>) => Promise<T>;

  function handleCancel() {
    state.cancelAction?.onClick?.();
    onClose();
  }

  async function handleConfirm() {
    try {
      setState((prev) => ({ ...prev, isLoading: true }));
      const result = (await state.confirmAction?.onClick?.()) ?? resultRef.current;
      resolversRef.current.resolve(result);
      onClose();
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }

  const setResult = useStableCallback((result: unknown) => void (resultRef.current = result));
  const setCanConfirm = useStableCallback(
    (canConfirm: boolean) => void setState((s) => ({ ...s, canConfirm })),
  );

  const contextValue = useMemo(() => ({ openDialog, closeDialog: onClose }), [openDialog, onClose]);
  const contextRef = useMemo(
    () => ({ setResult, setCanConfirm, closeDialog: onClose }),
    [onClose, setCanConfirm, setResult],
  );

  return (
    <AlertDialogContext.Provider value={contextValue}>
      {children}
      <AlertDialogRefContext.Provider value={contextRef}>
        <AlertDialog
          size="xl"
          isOpen={isOpen}
          leastDestructiveRef={cancelRef}
          onClose={onClose}
          isCentered={true}
          motionPreset="slideInBottom"
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg">{state.dialogHeader}</AlertDialogHeader>
              <AlertDialogCloseButton />
              <AlertDialogBody whiteSpace="pre-line">{state.dialogContent}</AlertDialogBody>
              <AlertDialogFooter gap={3}>
                <Button
                  ref={cancelRef}
                  variant="soft"
                  isDisabled={state.cancelAction?.isDisabled}
                  onPress={handleCancel}
                >
                  {state.cancelBtnLabel || state.cancelAction?.children || t('alert.cancel')}
                </Button>
                {/* Destructive stays the default, exactly as the old
                 * colorScheme="red" was: a config that overrides colorScheme
                 * to anything else opts out of the critical tone. */}
                <Button
                  variant="solid"
                  tone={
                    (state.confirmAction?.colorScheme ?? 'red') === 'red' ? 'critical' : undefined
                  }
                  onPress={handleConfirm}
                  isPending={state.isLoading || state.confirmAction?.isLoading}
                  isDisabled={
                    !state.canConfirm ||
                    state.confirmAction?.isDisabled ||
                    state.confirmAction?.disabled
                  }
                >
                  {state.confirmAction?.children || t('alert.confirm')}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </AlertDialogRefContext.Provider>
    </AlertDialogContext.Provider>
  );
};

export const useAlertDialog = () => useContext(AlertDialogContext);
export const useAlertDialogRef = () => useContext(AlertDialogRefContext);
export const useAlertDialogForm = <TFieldValues extends FieldValues>(
  form: UseFormReturn<TFieldValues>,
) => {
  const { setResult, setCanConfirm } = useAlertDialogRef();
  useEffect(() => form.watch(setResult).unsubscribe, [form, setResult]);
  useEffect(() => setResult(form.getValues()), [form, setResult]);
  useEffect(() => setCanConfirm(form.formState.isValid), [form.formState.isValid, setCanConfirm]);
};

/**
 * Create promise resolvers with suppressed `AbortError` rejections
 * to avoid unhandled promise rejection warnings
 */
function createPromiseResolvers() {
  const resolvers = Promise.withResolvers();
  // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
  resolvers.promise.catch((e) => (isAbortError(e) ? undefined : Promise.reject(e)));

  return resolvers;
}
